import { Link } from 'react-router-dom';
import { 
  FirstAidKit, 
  Robot, 
  Heart, 
  Shield, 
  ChartLineUp, 
  Users, 
  CaretRight,
  Lightning,
  Brain,
  ClipboardText
} from '@phosphor-icons/react';

const features = [
  {
    icon: Robot,
    title: 'AI-Powered Triage',
    description: 'Intelligent risk assessment and care gap detection during patient intake.',
  },
  {
    icon: ClipboardText,
    title: 'Smart Scribing',
    description: 'AI documentation that captures clinical encounters in real-time.',
  },
  {
    icon: Brain,
    title: 'Decision Support',
    description: 'Point-of-care insights surfaced directly in provider workflows.',
  },
  {
    icon: Lightning,
    title: 'Streamlined Workflows',
    description: 'Unified dashboard for CMAs, providers, and administrators.',
  },
];

const stats = [
  { value: '40%', label: 'Reduction in Admin Time' },
  { value: '25%', label: 'Faster Patient Throughput' },
  { value: '60%', label: 'Care Gaps Identified' },
  { value: '95%', label: 'Provider Satisfaction' },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <FirstAidKit size={32} weight="duotone" className="text-teal-600" />
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope' }}>
              T-MEDCMA
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link 
              to="/login" 
              data-testid="nav-login"
              className="text-slate-600 hover:text-teal-600 font-medium transition-colors"
            >
              Sign In
            </Link>
            <Link 
              to="/login" 
              data-testid="nav-get-started"
              className="btn-teal"
            >
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm font-medium mb-6">
                <Robot size={16} />
                AI-Enhanced Healthcare
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight mb-6" style={{ fontFamily: 'Manrope' }}>
                Modernize Care Delivery with{' '}
                <span className="text-gradient">Intelligent Workflows</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Empower your clinical team with AI-driven triage, automated documentation, 
                and decision-ready encounters. Reduce burnout, close care gaps, and improve patient outcomes.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/login" 
                  data-testid="hero-get-started"
                  className="btn-teal py-3 px-6 flex items-center gap-2"
                >
                  Start Free Trial
                  <CaretRight size={18} weight="bold" />
                </Link>
                <button 
                  className="btn-outline py-3 px-6"
                  data-testid="hero-demo"
                >
                  Watch Demo
                </button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758691463620-188ca7c1a04f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHx5b3VuZyUyMGRvY3RvciUyMHRlbGVoZWFsdGglMjB2aWRlbyUyMGNhbGwlMjBjb21wdXRlciUyMGRlc2slMjBtb2Rlcm58ZW58MHx8fHwxNzc3NzgxMTI5fDA&ixlib=rb-4.1.0&q=85"
                alt="Doctor on telehealth video call"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                    <Heart size={20} className="text-teal-600" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Care Gap Alert</div>
                    <div className="text-xs text-slate-500">Mammogram overdue</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
                  {stat.value}
                </div>
                <div className="text-slate-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
              Everything You Need to Transform Care
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our platform integrates AI capabilities at every step of the patient journey, 
              from intake to follow-up.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="ai-feature-card bg-white p-6 rounded-xl border border-slate-200 card-hover"
              >
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon size={24} className="text-teal-600" weight="duotone" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-6" style={{ fontFamily: 'Manrope' }}>
                Built for Every Member of Your Care Team
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">For Patients</h4>
                    <p className="text-sm text-slate-600">Easy intake forms, health records access, and secure messaging with your care team.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FirstAidKit size={20} className="text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">For Medical Assistants</h4>
                    <p className="text-sm text-slate-600">Streamlined triage workflows with AI-powered risk assessment and documentation support.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ChartLineUp size={20} className="text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">For Providers</h4>
                    <p className="text-sm text-slate-600">Decision-ready encounters with AI scribing, care gap alerts, and clinical decision support.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">For Administrators</h4>
                    <p className="text-sm text-slate-600">Complete oversight with analytics dashboards, user management, and operational insights.</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1576669802367-42e9fd83d9af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHw0fHxjbGluaWMlMjBzdGFmZiUyMHdvcmtpbmclMjBjb21wdXRlcnN8ZW58MHx8fHwxNzc4NzA5OTYyfDA&ixlib=rb-4.1.0&q=85"
                alt="Diverse CMAs working at computer workstations contacting patients"
                className="rounded-2xl shadow-lg"
                data-testid="features-cma-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
            Ready to Transform Your Practice?
          </h2>
          <p className="text-teal-100 mb-8 text-lg">
            Join healthcare organizations already using T-MEDCMA to improve outcomes and reduce burnout.
          </p>
          <Link 
            to="/login" 
            data-testid="cta-get-started"
            className="inline-flex items-center gap-2 bg-white text-teal-600 font-semibold px-8 py-4 rounded-full hover:bg-teal-50 transition-colors"
          >
            Get Started Today
            <CaretRight size={20} weight="bold" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-white">
              <FirstAidKit size={24} weight="duotone" />
              <span className="font-bold" style={{ fontFamily: 'Manrope' }}>T-MEDCMA</span>
            </div>
            <p className="text-slate-400 text-sm">
              2024 T-MEDCMA. Transforming healthcare with AI.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
